import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

const useResultsTableTable = ({
  columns,
  data,
  isMaximized,
  onOpenDetail,
  matchedTermColorMap,
  columnState,
  columnVisibility,
  columnOrder,
  columnPinning,
  setColumnVisibility,
  setColumnOrder,
  setColumnPinning,
  rowSelection,
  setRowSelection,
  grouping,
  setGrouping,
  columnFilters,
  setColumnFilters,
  globalFilter,
  setGlobalFilter,
  onRequestAddFilter,
  onResetFilters,
}) => {
  return useMaterialReactTable({
    columns,
    data,
    localization: MRT_Localization_EN,
    enableRowSelection: true,
    enableColumnOrdering: true,
    enableColumnDragging: false,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enablePagination: false,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: 'popover',
    enableFacetedValues: true,
    enableGrouping: true,
    enableRowVirtualization: true,
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
    onGroupingChange: setGrouping,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    state: {
      rowSelection,
      grouping,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnPinning,
    },
    initialState: {
      columnPinning:
        columnState.pinning.left.length > 0 || columnState.pinning.right.length > 0
          ? columnState.pinning
          : undefined,
      columnOrder: columnState.ordering.length > 0 ? columnState.ordering : undefined,
    },
    muiTableBodyCellProps: ({ cell }) => ({
      'data-testid': `results-cell-${cell.column.id}-${cell.row.id}`,
    }),
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table }) => {
      const canFilter = column.getCanFilter?.();
      if (!canFilter) return internalColumnMenuItems;

      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            onRequestAddFilter?.(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" style={{ marginRight: 8 }} />
          Add Filter
        </MenuItem>
      );

      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];

      const getMenuItemLabel = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getMenuItemLabel).join('');
        if (typeof node === 'object') {
          if (node.props?.label) return node.props.label;
          if (node.props?.children) return getMenuItemLabel(node.props.children);
        }
        return '';
      };

      const clearFilterLabel = table?.options?.localization?.clearFilter || 'Clear Filter';
      const normalizedClearFilterLabel = clearFilterLabel
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) => {
        const label = getMenuItemLabel(item);
        const normalizedLabel = label
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
        return normalizedLabel === normalizedClearFilterLabel;
      });

      if (clearFilterIndex === -1) {
        return [addFilterItem, ...internalItems];
      }
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        sx: {
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    renderToolbarInternalActions: ({ table }) => {
      const {
        columnFilterDisplayMode,
        enableColumnFilters,
        enableColumnOrdering,
        enableColumnPinning,
        enableDensityToggle,
        enableFilters,
        enableFullScreenToggle,
        enableGlobalFilter,
        enableHiding,
        initialState,
      } = table.options;
      const { columnFilters: activeColumnFilters, globalFilter: activeGlobalFilter } = table.getState();
      const hasActiveFilters = Boolean(activeGlobalFilter) || (activeColumnFilters?.length || 0) > 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {enableFilters && enableGlobalFilter && !(initialState?.showGlobalFilter) ? (
            <MRT_ToggleGlobalFilterButton table={table} />
          ) : null}
          <Tooltip title="Reset filters">
            <span>
              <IconButton size="small" onClick={() => onResetFilters?.(table)} disabled={!hasActiveFilters}>
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {enableFilters && enableColumnFilters && columnFilterDisplayMode !== 'popover' ? (
            <MRT_ToggleFiltersButton table={table} />
          ) : null}
          {(enableHiding || enableColumnOrdering || enableColumnPinning) ? (
            <MRT_ShowHideColumnsButton table={table} />
          ) : null}
          {enableDensityToggle ? <MRT_ToggleDensePaddingButton table={table} /> : null}
          {enableFullScreenToggle ? <MRT_ToggleFullScreenButton table={table} /> : null}
        </Box>
      );
    },
    renderTopToolbarCustomActions: () => null,
    muiTableContainerProps: {
      sx: {
        maxHeight: isMaximized ? 'calc(100vh - 280px)' : 640,
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const matchedIndex = row?.original?.matchedTermIndex;
      const highlightColor =
        typeof matchedIndex === 'number' && Number.isInteger(matchedIndex)
          ? matchedTermColorMap.get(matchedIndex)
          : null;
      return {
        hover: true,
        onClick: (event) => {
          const target = event?.target;
          if (target?.closest?.('button, a, input, textarea, [role="button"]')) return;
          onOpenDetail?.(row.original);
        },
        sx: highlightColor
          ? {
              backgroundColor: alpha(highlightColor, 0.12),
              borderLeft: `4px solid ${alpha(highlightColor, 0.7)}`,
              '&:hover': {
                backgroundColor: alpha(highlightColor, 0.2),
              },
            }
          : undefined,
      };
    },
  });
};

export default useResultsTableTable;
